import { LinearProgress as MuiLinearProgress, type LinearProgressProps as MuiLinearProgressProps } from '@mui/material';
import { ProgressPrimitive } from '@figma/fpl-components/beta';
import { color as token, borderRadius } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { forwardRef, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { I18N_KEYS } from '@/language/keys';

const DEFAULT_HEIGHT_PX = 4;

/**
 * FPL-native configuration for the `fpl-migration`-on branch. Callers pass it without
 * checking the flag; the legacy MUI branch ignores the entire bucket.
 */
export interface LinearProgressFplProps {
  /**
   * Accessible name for the progressbar. FPL's `ProgressPrimitive` renders no visible
   * label of its own, so a name is required; defaults to a translated "Upload progress".
   */
  'aria-label'?: string;
  /** Bar thickness in px. @default 4 */
  height?: number;
}

export interface LinearProgressProps extends MuiLinearProgressProps {
  /** Configuration read only by the `fpl-migration`-on branch; ignored by the legacy branch. */
  fplProps?: LinearProgressFplProps;
}

const indeterminateSlide = stylex.keyframes({
  '0%': { insetInlineStart: '-40%' },
  '100%': { insetInlineStart: '100%' },
});

const styles = stylex.create({
  track: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    backgroundColor: token.bgSecondary,
    borderRadius: borderRadius.full,
  },
  fillDeterminate: {
    height: '100%',
    backgroundColor: token.bgBrand,
    borderRadius: borderRadius.full,
    transition: 'width 200ms ease',
  },
  fillIndeterminate: {
    position: 'absolute',
    insetBlockStart: 0,
    height: '100%',
    width: '40%',
    backgroundColor: token.bgBrand,
    borderRadius: borderRadius.full,
    animationName: indeterminateSlide,
    animationDuration: '1.4s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },
});

/**
 * Determinate/indeterminate linear progress bar and the `fpl-migration` boundary.
 *
 * - Flag off: the legacy MUI `LinearProgress`, with every existing prop
 *   (`variant`, `value`, `color`, `sx`, …) unchanged.
 * - Flag on: FPL's headless `ProgressPrimitive` (accessible `role="progressbar"`) with a
 *   locally-styled track/fill using FPL tokens. `color` and `sx` are legacy-only — flag-on
 *   color is owned by the FPL brand token; use `fplProps` for flag-on configuration.
 *
 * Only `determinate` and `indeterminate` are distinguished in the flag-on branch; the MUI
 * `buffer`/`query` variants (unused in production) render as indeterminate.
 */
export const LinearProgress = forwardRef<HTMLElement, LinearProgressProps>(function LinearProgress(
  { fplProps, ...muiProps },
  ref,
) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const { t: translate } = useTranslation();

  if (isFplMigrationEnabled) {
    const isDeterminate = (muiProps.variant ?? 'indeterminate') === 'determinate';
    const rawValue = typeof muiProps.value === 'number' ? muiProps.value : 0;
    const value = Math.min(100, Math.max(0, rawValue));
    const height = fplProps?.height ?? DEFAULT_HEIGHT_PX;
    const label = fplProps?.['aria-label'] ?? translate(I18N_KEYS.COMMON_COMPONENTS.PROGRESS.DEFAULT_ARIA);
    const trackProps = stylex.props(styles.track);

    return (
      <ProgressPrimitive
        ref={ref as Ref<HTMLDivElement>}
        aria-label={label}
        value={isDeterminate ? value : undefined}
        max={100}
        className={trackProps.className}
        style={{ ...trackProps.style, height }}
      >
        {isDeterminate ? (
          <div {...stylex.props(styles.fillDeterminate)} style={{ width: `${value}%` }} />
        ) : (
          <div {...stylex.props(styles.fillIndeterminate)} />
        )}
      </ProgressPrimitive>
    );
  }

  return <MuiLinearProgress ref={ref} {...muiProps} />;
});
